(() => {
  function initVideoRail(root) {
    const rail = root.querySelector('[data-video-rail]');
    if (!rail) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    let raf = 0;
    let running = false;
    let onScreen = true;
    let resumeTimer = null;
    let edgePauseUntil = 0;
    let direction = 1;
    let lastWritten = 0;
    let fingerDown = false;
    let lastUserDir = 0;
    let touchStartX = 0;
    let touchStartLeft = 0;
    const speed = 0.5;
    const edgePauseMs = 1400;
    const resumeMs = 2800;

    const stopLoop = () => {
      running = false;
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    const maxScroll = () => Math.max(0, rail.scrollWidth - rail.clientWidth);

    const tick = () => {
      if (!running || !onScreen || fingerDown) {
        raf = 0;
        return;
      }

      const max = maxScroll();
      if (max <= 4) {
        raf = window.requestAnimationFrame(tick);
        return;
      }

      const current = rail.scrollLeft;
      if (Math.abs(current - lastWritten) > Math.max(2.5, speed * 4)) {
        lastUserDir = current < lastWritten ? -1 : 1;
        direction = lastUserDir;
        lastWritten = current;
        stopLoop();
        scheduleResume();
        return;
      }

      if (performance.now() >= edgePauseUntil) {
        let next = current + speed * direction;
        if (next >= max) {
          next = max;
          direction = -1;
          lastUserDir = -1;
          edgePauseUntil = performance.now() + edgePauseMs;
        } else if (next <= 0) {
          next = 0;
          direction = 1;
          lastUserDir = 1;
          edgePauseUntil = performance.now() + edgePauseMs;
        }
        rail.scrollLeft = next;
        lastWritten = rail.scrollLeft;
      }

      raf = window.requestAnimationFrame(tick);
    };

    const startLoop = () => {
      if (fingerDown || !onScreen) return;
      stopLoop();
      running = true;
      lastWritten = rail.scrollLeft;
      raf = window.requestAnimationFrame(tick);
    };

    const scheduleResume = (delay = resumeMs) => {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(() => {
        if (fingerDown) return;
        const max = maxScroll();
        const left = rail.scrollLeft;

        if (lastUserDir < 0 && left > 6) {
          direction = -1;
          startLoop();
          return;
        }

        if (left <= 6) {
          direction = 1;
          lastUserDir = 1;
          rail.scrollLeft = 0;
          lastWritten = 0;
          edgePauseUntil = performance.now() + edgePauseMs;
        } else if (left >= max - 6) {
          direction = -1;
          lastUserDir = -1;
          edgePauseUntil = performance.now() + edgePauseMs;
        } else if (lastUserDir !== 0) {
          direction = lastUserDir;
        }

        startLoop();
      }, delay);
    };

    const holdForUser = () => {
      window.clearTimeout(resumeTimer);
      stopLoop();
      lastWritten = rail.scrollLeft;
    };

    rail.addEventListener(
      'touchstart',
      (e) => {
        fingerDown = true;
        touchStartX = e.changedTouches[0]?.clientX ?? 0;
        touchStartLeft = rail.scrollLeft;
        holdForUser();
      },
      { passive: true, capture: true }
    );

    rail.addEventListener(
      'touchmove',
      () => {
        holdForUser();
      },
      { passive: true, capture: true }
    );

    const releaseFinger = (e) => {
      const x = e.changedTouches?.[0]?.clientX;
      if (typeof x === 'number') {
        const dx = x - touchStartX;
        const dScroll = rail.scrollLeft - touchStartLeft;
        if (Math.abs(dScroll) > 2) lastUserDir = dScroll > 0 ? 1 : -1;
        else if (Math.abs(dx) > 10) lastUserDir = dx < 0 ? 1 : -1;
      }
      fingerDown = false;
      lastWritten = rail.scrollLeft;
      scheduleResume();
    };
    rail.addEventListener('touchend', releaseFinger, { passive: true, capture: true });
    rail.addEventListener('touchcancel', releaseFinger, { passive: true, capture: true });

    rail.addEventListener('mouseenter', () => {
      if (window.matchMedia('(hover: hover)').matches) holdForUser();
    });
    rail.addEventListener('mouseleave', () => {
      if (window.matchMedia('(hover: hover)').matches) {
        fingerDown = false;
        scheduleResume(1200);
      }
    });

    rail.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 2) {
          holdForUser();
          lastUserDir = e.deltaX > 0 ? 1 : -1;
          direction = lastUserDir;
          scheduleResume(2000);
        }
      },
      { passive: true }
    );

    // Let the first tap unlock iframe playback; keep rail scrollable otherwise
    rail.querySelectorAll('.video-card').forEach((card) => {
      const media = card.querySelector('iframe, video');
      if (!media) return;
      media.classList.add('is-inert');
      card.addEventListener('click', () => {
        holdForUser();
        media.classList.remove('is-inert');
      });
      if (media.tagName === 'VIDEO') {
        media.addEventListener('play', holdForUser);
        media.addEventListener('pause', () => scheduleResume(2200));
        media.addEventListener('ended', () => scheduleResume(1200));
      }
    });

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            onScreen = entry.isIntersecting;
            if (!onScreen) holdForUser();
            else if (!fingerDown) scheduleResume(800);
          });
        },
        { threshold: 0.12 }
      );
      io.observe(root);
    }

    startLoop();
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-video-reviews]').forEach(initVideoRail);
  });
})();
